import React, { LegacyRef, MouseEventHandler } from 'react'
import { LanguageIdLanguages } from '../../language'
import { LanguageContext } from '../../contexts'
import { getFriendlyDurationToEventCutoff, shouldShowRegistrationCutoffNotice, WorkshopEvent } from '../../Models/workshops'
import { format } from '../../format'
import { fillTemplateString } from '../../utility'
import { Icons, WorkshopStatusIds, WorkshopStatusIdStatuses, WorkshopTypeIds } from '../../constants'

import classNames from 'classnames'

import './EventCollapsed.scss'
import { ParticipationProgress } from './ParticipationProgress'

export interface EventCollapsedProps {
	event: WorkshopEvent
	onClick: MouseEventHandler<HTMLDivElement>
}

export const EventCollapsed = React.forwardRef((props: EventCollapsedProps, ref: LegacyRef<HTMLDivElement>) => {
	const { localizedText } = React.useContext(LanguageContext)

	const scheduledDays = props.event.Schedule.map((schedule, scheduleIndex) => {
		return (
			<div key={scheduleIndex}>
				{format.scheduleInterval(schedule)}
			</div>
		)
	})

	let participationContent = 
		<div className='participation-progress is-pulled-right'>
			{fillTemplateString(localizedText.SeatsAvailableTemplate, props.event)}
			<ParticipationProgress seatsLeft={props.event.SeatsLeft} maximumParticipants={props.event.MaximumParticipants} />
		</div>

	if (props.event.IsParticipating) participationContent = 
		<span className='icon-text has-text-weight-bold'>
			{localizedText.Registered}
			<span className='icon has-text-success ml-2 is-size-4'>
				<i>{Icons.checkmark}</i>
			</span>
		</span>


	let tentativeStatusIndicator = (props.event.StatusId == WorkshopStatusIds.Tentative) ? 
		<span className='tag is-warning is-medium ml-2' title={localizedText.ThisWorkshopIsTentativelyScheduled}>{localizedText[WorkshopStatusIdStatuses[props.event.StatusId]]}</span> : 
		null

	let location = <><span>{props.event.Location.Name}</span>{tentativeStatusIndicator}</>

	if (props.event.WorkshopTypeId == WorkshopTypeIds.Online) {
		location = <><span>{localizedText['Online']} {Icons.laptop}  </span>{tentativeStatusIndicator}</>
	}

	let cutoffNotice: React.ReactElement | null = null
	let cutoffNoticeColor = shouldShowRegistrationCutoffNotice(props.event)
	if (cutoffNoticeColor) {
		const friendlyDuration = getFriendlyDurationToEventCutoff(props.event)
		const intervalMessage = fillTemplateString(localizedText.RegistrationCutoffNoticeTemplate, { friendlyDuration })
		cutoffNotice = 
			<div className='column has-text-centered'>
				<span className={`tag is-${cutoffNoticeColor} is-medium ml-2`}>{intervalMessage}</span> 
			</div>
	}

	const workshopIsOnline = props.event.WorkshopTypeId == WorkshopTypeIds.Online

	const locationAddress = workshopIsOnline ? null : 
		<p>{props.event.Location.FullAddress}</p>

	const showCounty = !workshopIsOnline && !!props.event.Location.MunicipalityTier1Name

	const county = !showCounty ? null : 
		<p>{localizedText.County}: {props.event.Location.MunicipalityTier1Name}</p>

	const cardContentClass = classNames('columns', { 'has-background-success-light': props.event.IsParticipating })

	return (
		<div ref={ref} className='block is-clickable workshop-event' onClick={props.onClick}>
			<div className='card'>
				<div className='card-header'>
					<div className='columns is-clearfix'>
						<div className='column'>
							{localizedText.Language}: {localizedText[LanguageIdLanguages[props.event.LanguageId]]}
						</div>
						<div className='column has-text-centered is-size-5'>
							{localizedText.WorkshopLeader}: {props.event.Instructor.FirstName} {props.event.Instructor.LastName}
						</div>
						<div className='column has-text-right'>
							{participationContent}
						</div>
					</div>
				</div>

				<div className='card-content'>
					<div className={cardContentClass}>
						<div className='column'>
							{scheduledDays}
						</div>
						{cutoffNotice}
						<div className='column has-text-right'>
							{location}
							{county}
							{locationAddress}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
})

export default EventCollapsed
